import { Injectable, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import {
    Firestore,
    collection,
    deleteDoc,
    doc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    updateDoc,
    where,
    writeBatch,
} from '@angular/fire/firestore';
import { Observable, ReplaySubject, from } from 'rxjs';

import { MODEL_APP_USERS, MODEL_USER_NOTIFICATIONS } from '@aveespa/common/constants/models-metadata.constants';
import type { UserNotification, StepAssigneeDeactivatedPayload, PlatformTemplateAssignedPayload, PlatformTemplateVersionAvailablePayload } from '@aveespa/common/models/notifications/user-notification.interface';
import { Notification } from './notifications.types';

@Injectable({ providedIn: 'root' })
export class NotificationsService {

    private readonly _firestore = inject(Firestore);
    private readonly _auth      = inject(Auth);

    private readonly _notifications$ = new ReplaySubject<Notification[]>(1);
    private _unsub: (() => void) | null = null;

    get notifications$(): Observable<Notification[]> {
        return this._notifications$.asObservable();
    }

    /**
     * Opens a real-time Firestore listener on the current user's notifications
     * subcollection. Call once after the user is authenticated (from the app
     * resolver). Safe to call again — stops the previous listener first.
     */
    startListening(): void {
        this.stopListening();

        const uid = this._auth.currentUser?.uid;
        if (!uid) {
            this._notifications$.next([]);
            return;
        }

        const q = query(
            collection(this._firestore, MODEL_APP_USERS, uid, MODEL_USER_NOTIFICATIONS),
            orderBy('createdAt', 'desc'),
        );

        this._unsub = onSnapshot(
            q,
            (snap) => {
                const notifs = snap.docs.map(d => this._map({ ...d.data(), id: d.id } as UserNotification));
                this._notifications$.next(notifs);
            },
            () => {
                // Listener can fail transiently during auth token propagation.
                // Emit empty so the UI doesn't hang; the listener retries on next startListening() call.
                this._notifications$.next([]);
            },
        );
    }

    stopListening(): void {
        if (this._unsub) {
            this._unsub();
            this._unsub = null;
        }
    }

    markAllAsRead(): Observable<void> {
        return from(this._markAllAsRead());
    }

    update(id: string, read: boolean): Observable<void> {
        const uid = this._auth.currentUser?.uid;
        if (!uid) return from(Promise.resolve());
        return from(updateDoc(
            doc(this._firestore, MODEL_APP_USERS, uid, MODEL_USER_NOTIFICATIONS, id),
            { read },
        ));
    }

    delete(id: string): Observable<void> {
        const uid = this._auth.currentUser?.uid;
        if (!uid) return from(Promise.resolve());
        return from(deleteDoc(
            doc(this._firestore, MODEL_APP_USERS, uid, MODEL_USER_NOTIFICATIONS, id),
        ));
    }

    // ── Private ───────────────────────────────────────────────────────────────

    private async _markAllAsRead(): Promise<void> {
        const uid = this._auth.currentUser?.uid;
        if (!uid) return;

        const snap = await getDocs(query(
            collection(this._firestore, MODEL_APP_USERS, uid, MODEL_USER_NOTIFICATIONS),
            where('read', '==', false),
        ));

        if (snap.empty) return;

        const batch = writeBatch(this._firestore);
        snap.docs.forEach(d => batch.update(d.ref, { read: true }));
        await batch.commit();
    }

    private _map(n: UserNotification): Notification {
        const base: Notification = {
            id:   n.id!,
            read: n.read,
            time: new Date(n.createdAt).toISOString(),
        };

        switch (n.type) {
            case 'step_assignee_deactivated': {
                const p = n.payload as StepAssigneeDeactivatedPayload;
                return {
                    ...base,
                    icon:        'heroicons_outline:user-minus',
                    title:       'Step reassignment needed',
                    description: `${p.deactivatedUserName} was deactivated and had an active step. Open the request to reassign.`,
                    link:        `/requests/${p.requestId}`,
                    useRouter:   true,
                };
            }
            case 'queue_escalation': {
                return {
                    ...base,
                    icon:        'heroicons_outline:exclamation-triangle',
                    title:       'Queue needs attention',
                    description: 'A request is waiting but no eligible reviewers are assigned to the queue.',
                    link:        '/requests/office-queue',
                    useRouter:   true,
                };
            }
            case 'platform_template_assigned': {
                const p = n.payload as PlatformTemplateAssignedPayload;
                return {
                    ...base,
                    icon:        'heroicons_outline:document-duplicate',
                    title:       'New template assigned',
                    description: `A new template "${p.platformTemplateName}" has been assigned to your organization. Open the template builder to review and configure it.`,
                    link:        `/admin/templates/${p.newTemplateId}/edit`,
                    useRouter:   true,
                };
            }
            case 'platform_template_version_available': {
                const p = n.payload as PlatformTemplateVersionAvailablePayload;
                return {
                    ...base,
                    icon:        'heroicons_outline:arrow-up-circle',
                    title:       'Template update available',
                    description: `"${p.platformTemplateName}" has a new version (v${p.platformTemplateVersion}). Review the changes in your templates list.`,
                    link:        '/admin/templates',
                    useRouter:   true,
                };
            }
            default:
                return { ...base, icon: 'heroicons_outline:bell', title: 'Notification' };
        }
    }
}
